/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { useSelector } from 'react-redux';
import {
    bool,
    oneOf,
    node,
    number,
    string,
} from 'prop-types';

import FilterSvgIcon from '../../BrandTheme/Icons/FilterSvgIcon';
import DropDownIcon from '../../BrandTheme/Icons/DropDownIcon';
import SortByIcon from '../../BrandTheme/Icons/SortByIcon';
import { getBrand } from '../../../pages/Account/state/ducks/Common/Common-Selectors';
import brandColors from './brandColors';

const useStyles = makeStyles((theme) => ({
    root: {
        position: 'relative',
        boxSizing: 'border-box',
        padding: '12px',
        borderRadius: '36px',
        transition: 'background-color 0.2s ease, color 0.2s ease, opacity 0.2s ease',
    },
    disabled: {
        opacity: 0.45,
        cursor: 'not-allowed',
    },
    content: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '8px',
        width: '100%',
        whiteSpace: 'nowrap',
        lineHeight: 1,
    },
    label: {
        display: 'inline-flex',
        alignItems: 'center',
        fontSize: '14px',
        fontWeight: 400,
        lineHeight: '20px',
        whiteSpace: 'nowrap',
        position: 'relative',
        top: '-1px',
    },
    startIcon: {
        width: '20px',
        height: '20px',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        lineHeight: 0,
        '& svg': {
            display: 'block',
            verticalAlign: 'middle',
        },
    },
    endIcon: {
        width: '20px',
        height: '20px',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        lineHeight: 0,
        '& svg': {
            display: 'block',
            verticalAlign: 'middle',
        },
    },
    badge: {
        position: 'absolute',
        top: '-7px',
        right: '-5px',
        width: '20px',
        height: '20px',
        borderRadius: '20px',
        backgroundColor: ({ pillColor }) => pillColor,
        color: '#ffffff',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '12px',
        lineHeight: 1,
    },
    chip: {
        backgroundColor: ({ chipBg }) => chipBg || theme?.palette?.highlightColor || '#F5F5F5',
        color: ({ pillColor }) => pillColor,
        minHeight: '36px',
        height: '36px',
        padding: '0 12px 0 16px',
        borderRadius: '999px',
        '& $content': {
            height: '100%',
        },
        '& $label': {
            top: '0',
        },
        '& $endIcon': {
            width: '16px',
            height: '16px',
            '& svg': {
                width: '9px',
                height: '9px',
            },
        },
        '& svg *': {
            fill: ({ pillColor }) => pillColor,
            stroke: ({ pillColor }) => pillColor,
        },
    },
    active: {
        color: ({ pillColor }) => pillColor,
        border: ({ pillColor }) => `1px solid ${pillColor}`,
        '& svg *': {
            fill: ({ pillColor }) => pillColor,
            stroke: ({ pillColor }) => pillColor,
        },
    },
    inactive: {
        color: '#000000',
        border: '1px solid #D9D9D9',
        '& svg *': {
            fill: '#000000',
            stroke: '#000000',
        },
    },
}));

const getEndIconByType = (endIconType) => {
    switch (endIconType) {
        case 'filter':
            return <FilterSvgIcon version="v2" />;
        case 'sortby':
            return <SortByIcon />;
        case 'dropdown':
            return <DropDownIcon />;
        default:
            return null;
    }
};

const getRootClasses = (classes, disabled, variant) => [
    classes.root,
    disabled ? classes.disabled : '',
    variant === 'chip' ? classes.chip : '',
    variant === 'active' ? classes.active : '',
    variant === 'inactive' ? classes.inactive : '',
].filter(Boolean).join(' ');

const renderBadge = (classes, badgeCount) => {
    if (badgeCount === null || badgeCount === undefined || badgeCount === 0) {
        return null;
    }
    const displayValue = badgeCount > 9 ? '9+' : `${badgeCount}`;
    return <span className={classes.badge} aria-hidden="true">{displayValue}</span>;
};

const Pill = ({
    label,
    disabled,
    badgeCount,
    startIcon,
    endIcon,
    endIconType,
    ariaLabel,
    variant,
}) => {
    const brand = useSelector(getBrand);
    const classes = useStyles({
        pillColor: brandColors?.[brand?.domain]?.primary || '#2F2F2F',
        chipBg: brandColors?.[brand?.domain]?.chipBackground,
    });
    const computedVariant = variant || 'inactive';
    const computedEndIcon = endIcon || getEndIconByType(endIconType);

    return (
        <div
            className={getRootClasses(classes, disabled, computedVariant)}
        >
            <span className={classes.content}>
                {startIcon && <span className={classes.startIcon}>{startIcon}</span>}
                <span className={classes.label}>{label}</span>
                {computedEndIcon && <span className={classes.endIcon}>{computedEndIcon}</span>}
            </span>
            {renderBadge(classes, badgeCount)}
        </div>
    );
};

Pill.propTypes = {
    label: string.isRequired,
    disabled: bool,
    badgeCount: number,
    startIcon: node,
    endIcon: node,
    endIconType: oneOf(['filter', 'sortby', 'dropdown']),
    ariaLabel: string,
    variant: oneOf(['inactive', 'active', 'chip']),
};

Pill.defaultProps = {
    disabled: false,
    badgeCount: null,
    startIcon: null,
    endIcon: null,
    endIconType: 'filter',
    ariaLabel: '',
    variant: '',
};

export default Pill;
